import React, { useRef } from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { SectionProps } from '../types/SectionProps';

const HeroSection: React.FC<SectionProps> = ({ id }) => {
  const theme = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  // Animation variants pour les éléments de fond
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1.2 } }
  };

  // Animation pour le chemin (la bonne voie)
  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { 
      pathLength: 1, 
      opacity: 1, 
      transition: { 
        pathLength: { duration: 2.5, ease: "easeInOut" },
        opacity: { duration: 0.5 }
      }
    }
  };
  
  // Animation pour les points lumineux sur le chemin
  const dotVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: (i: number) => ({
      scale: 1,
      opacity: [0, 1, 0.8, 1],
      transition: { 
        delay: i * 0.4,
        duration: 2,
        repeat: Infinity,
        repeatType: "reverse" as const
      }
    })
  };

  return (
    <Box
      id={id}
      ref={ref}
      sx={{
        position: 'relative',
        height: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
        minHeight: { xs: '500px', md: '700px' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        bgcolor: 'background.default',
        scrollMarginTop: '64px', // Pour le défilement fluide
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 3, alignItems: 'center' }}>
          <Box>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  mb: 2,
                  textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                }}
              >
                BONAVIA
              </Typography>
              
              <Box sx={{ position: 'relative', mb: 4, maxWidth: '600px' }}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    color: 'white',
                    fontWeight: 400,
                    fontFamily: '"Tenor Sans", sans-serif',
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  Faites les bons choix, suivez la bonne voie
                </Typography>
                <Box 
                  sx={{ 
                    width: '60px', 
                    height: '3px', 
                    bgcolor: 'error.main',
                    mt: 2,
                    mb: 4
                  }} 
                />
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  mb: 4,
                  maxWidth: '600px',
                  lineHeight: 1.6,
                  fontWeight: 300,
                  textShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
                }}
              >
                Conseil immobilier et accompagnement patrimonial avec le cœur.
                Une approche humaine, indépendante et engagée pour vos projets.
              </Typography>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  href="#services"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderRadius: '2px',
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.2)',
                      bgcolor: '#c9ad6e',
                    },
                  }}
                >
                  Découvrir nos services
                </Button>
              </motion.div>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Arrière-plan élégant avec animation "la bonne voie" */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
        <motion.div
          variants={backgroundVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            opacity: 0.92,
            zIndex: -1
          }}
        />
        
        {/* Animation "la bonne voie" */}
        <Box sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: -1, overflow: 'hidden' }}>
          <svg width="100%" height="100%" viewBox="0 0 1000 600" style={{ position: 'absolute', opacity: 0.15 }}>
            {/* Chemin principal symbolisant "la bonne voie" */}
            <motion.path
              d="M 100 400 Q 250 100 400 350 T 700 300 Q 850 250 900 100"
              stroke={theme.palette.secondary.main}
              strokeWidth="8"
              fill="transparent"
              variants={pathVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            />
            
            {/* Points lumineux le long du chemin */}
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <motion.circle
                key={i}
                cx={100 + (i * 160)}
                cy={400 - (i % 2 === 0 ? 100 : 0) - (i === 2 || i === 3 ? 50 : 0) - (i === 5 ? 300 : 0)}
                r={i === 0 || i === 5 ? 8 : 5}
                fill={i === 0 || i === 5 ? theme.palette.error.main : theme.palette.secondary.main}
                custom={i}
                variants={dotVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              />
            ))}
          </svg>
        </Box>
        
        {/* Overlay doré subtil */}
        <motion.div
          variants={backgroundVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            background: `linear-gradient(135deg, transparent 0%, rgba(213, 185, 123, 0.08) 100%)`,
            zIndex: -1
          }}
        />
      </Box>
      
      {/* Petit accent fuchsia en bas de la section hero */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '5px',
          bgcolor: 'error.main',
          zIndex: 1
        }}
      />
    </Box>
  );
};

export default HeroSection;
